import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function SceneCTA() {
  const root = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          end: "top 10%",
          scrub: 1,
        },
      });

      tl.from(".cta-remi-mark", {
        scale: 3,
        opacity: 0,
        duration: 1,
      });

      tl.from(".cta-line", {
        yPercent: 60,
        opacity: 0,
        stagger: 0.1,
        duration: 0.4,
      }, 0.3);

      tl.from(".cta-cropped-r", {
        yPercent: 100,
        duration: 1.2,
      }, 0.4);
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="scene-cta"
      ref={root}
      data-theme="dark"
      className="relative min-h-screen w-full overflow-hidden ink-bg grain"
    >
      <div className="pt-24 md:pt-32 px-6 md:px-16">
        <div className="type-label text-[color:var(--gray)] flex items-center justify-between">
          <span>07 / FINAL SIGNAL</span>
          <span>NOW TAKING PROJECTS</span>
        </div>

        <div className="cta-remi-mark mt-8 type-display text-[color:var(--signal)]" style={{ fontSize: "10vw", lineHeight: 0.9 }}>
          REMI
        </div>

        <div className="mt-4 max-w-[800px]">
          <div className="cta-line type-display text-[color:var(--paper)]" style={{ fontSize: "clamp(2.5rem, 5vw, 6rem)", lineHeight: 0.95 }}>
            NOW TAKING PROJECTS.
          </div>
          <div className="cta-line type-mono text-sm text-[color:var(--gray)] mt-6 max-w-[520px]">
            Let&apos;s build something autonomous. Independent full-stack systems, agent-powered execution, delivered end to end by one operator.
          </div>
        </div>

        <div className="mt-14 flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-14 cta-line">
          <a
            href="mailto:hello@remi.ai"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className="group inline-flex items-center gap-4 border-b border-[color:var(--paper)] pb-2 whitespace-nowrap cursor-pointer"
          >
            <span className="type-display text-[color:var(--paper)] text-2xl md:text-4xl">START A PROJECT</span>
            <span
              className="inline-block transition-transform"
              style={{ transform: `translateX(${hover ? 8 : 0}px)` }}
            >
              <i className="ri-arrow-right-line text-[color:var(--signal)] text-3xl" />
            </span>
          </a>

          <div className="type-mono text-xs text-[color:var(--gray)]">
            <div>hello@remi.ai</div>
            <div className="mt-1 text-[color:var(--paper)]">RESPONSE WITHIN 24H</div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 type-mono text-[10px] text-[color:var(--gray)]">
          <div>
            <div className="mb-2 text-[color:var(--paper)]">STACK</div>
            <div>NEXT · REACT · PYTHON · GO</div>
          </div>
          <div>
            <div className="mb-2 text-[color:var(--paper)]">MODELS</div>
            <div>OPEN · CLOSED · FINETUNED</div>
          </div>
          <div>
            <div className="mb-2 text-[color:var(--paper)]">LOCATION</div>
            <div>REMOTE / GLOBAL</div>
          </div>
          <div>
            <div className="mb-2 text-[color:var(--paper)]">AVAILABILITY</div>
            <div>Q3 · Q4 · 2026</div>
          </div>
        </div>
      </div>

      {/* huge cropped R below viewport */}
      <div className="relative w-full overflow-hidden pointer-events-none mt-16" style={{ height: "45vh" }}>
        <div
          className="cta-cropped-r absolute left-0 right-0 -bottom-[35vh] type-display text-[color:var(--dark)] text-center"
          style={{ fontSize: "70vw", lineHeight: 0.8 }}
        >
          R
        </div>
      </div>

      <footer className="relative z-10 px-6 md:px-16 pb-8 pt-6 flex items-center justify-between border-t border-[color:var(--dark)] type-label text-[color:var(--gray)]">
        <span>© 2026 REMI / AI</span>
        <span>SOLO OPERATOR · SHIPPING NOW</span>
        <span>END OF REEL</span>
      </footer>
    </section>
  );
}